import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { HoverPopoverIcon } from '@/components/hover-popover-icon';
import { useI18nContext } from '@/i18n/i18n-react';
import { getIncidentSlugForCampaignLabel } from '@/lib/campaign-label-incidents';

type CampaignLabelsIconProps = {
  labels: string[];
  closeOnScroll?: boolean;
};

export function CampaignLabelsIcon({
  labels,
  closeOnScroll = false,
}: CampaignLabelsIconProps) {
  const { LL } = useI18nContext();
  const { tenantSlug } = useParams();
  const tenantSlugParam =
    typeof tenantSlug === 'string' ? tenantSlug : undefined;

  if (labels.length === 0) {
    return null;
  }

  const labelMessages = labels
    .map((label) => renderLabelContent(label, LL, tenantSlugParam))
    .filter(
      (content): content is NonNullable<typeof content> => content !== null
    );

  if (labelMessages.length === 0) {
    return null;
  }

  const ariaLabel = LL.campaigns.labels.ariaLabel({
    count: labelMessages.length,
  });

  const message =
    labelMessages.length === 1 ? (
      labelMessages[0]
    ) : (
      <ul className="flex list-disc flex-col gap-2 pl-4">
        {labelMessages.map((content, index) => (
          <li key={index}>{content}</li>
        ))}
      </ul>
    );

  return (
    <HoverPopoverIcon
      icon={Info}
      ariaLabel={ariaLabel}
      message={message}
      variant="primary"
      closeOnScroll={closeOnScroll}
    />
  );
}

type LabelRenderer = (
  LL: ReturnType<typeof useI18nContext>['LL'],
  tenantSlug: string | undefined
) => ReactNode;

const labelRenderers: Record<string, LabelRenderer> = {
  events_data_loss_aug_2026: (LL, tenantSlug) => {
    const incidentSlug = getIncidentSlugForCampaignLabel(
      'events_data_loss_aug_2026'
    );
    const incidentLink =
      tenantSlug && incidentSlug
        ? `/${tenantSlug}/incidents/${incidentSlug}`
        : undefined;

    return (
      <p>
        {LL.campaigns.labels.events_data_loss_aug_2026.body()}{' '}
        {incidentLink ? (
          <Link to={incidentLink} className="underline">
            {LL.campaigns.labels.events_data_loss_aug_2026.link()}
          </Link>
        ) : (
          <span className="underline">
            {LL.campaigns.labels.events_data_loss_aug_2026.link()}
          </span>
        )}
      </p>
    );
  },
};

function renderLabelContent(
  label: string,
  LL: ReturnType<typeof useI18nContext>['LL'],
  tenantSlug: string | undefined
) {
  const renderer = labelRenderers[label];
  return renderer ? renderer(LL, tenantSlug) : null;
}
